import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { KatexRenderer } from '@quebon/shared-learning/utils'
import { KatexText } from '@quebon/shared-learning'
import { formatAnswer } from '../formatAnswer'

interface Props {
  order?: number
  isCorrect?: boolean | null
  partialCredit?: number | null
  /** 학습자가 본 문제 본문 (snapshot). null/undefined면 "준비 중" placeholder */
  questionSnapshot?: unknown
  studentAnswer?: unknown
  correctAnswer?: unknown
  explanation?: string
  /** 'mirror' = 학습자 화면 시점 (정답 숨김), 'meta' = 코칭 시점 (정답·해설 노출) */
  variant: 'mirror' | 'meta'
  /** 학생 답이 없는 시점(예: 교사 종이 채점 테스트, Phase 5b)에 "학생 응답" 행을 숨긴다. */
  hideStudentAnswer?: boolean
}

/** 문제 스냅샷에서 사람이 읽는 본문 1건을 고른다(problem → questionText → question → stem). */
export function snapshotText(snapshot: unknown): string | null {
  if (!snapshot || typeof snapshot !== 'object') return null
  const snap = snapshot as {
    problem?: string
    questionText?: string
    question?: string
    stem?: string
  }
  return snap.problem ?? snap.questionText ?? snap.question ?? snap.stem ?? null
}

function snapshotChoices(snapshot: unknown): Array<{ key: string; answer: string }> {
  if (!snapshot || typeof snapshot !== 'object') return []
  const candidates = (snapshot as { choices?: unknown; answers?: unknown }).choices
    ?? (snapshot as { answers?: unknown }).answers
  if (!Array.isArray(candidates)) return []
  return candidates.flatMap((candidate) => {
    if (!candidate || typeof candidate !== 'object') return []
    const { key, answer } = candidate as { key?: unknown; answer?: unknown }
    if (typeof answer !== 'string') return []
    return [{ key: String(key ?? answer), answer }]
  })
}

export function hasDisplayableCorrectAnswer(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.some(hasDisplayableCorrectAnswer)
  if (typeof value === 'object') {
    const answer = (value as { answer?: unknown }).answer
    return answer !== undefined
      ? hasDisplayableCorrectAnswer(answer)
      : Object.keys(value).length > 0
  }
  return true
}

export function displayCorrectAnswer(
  correctAnswer: unknown,
  questionSnapshot: unknown,
  booleanLabels?: { true: string; false: string },
): string {
  const isBoolean = questionSnapshot != null
    && typeof questionSnapshot === 'object'
    && (questionSnapshot as { type?: unknown }).type === 'boolean'
  const choices = snapshotChoices(questionSnapshot)
  const labelsByKey = new Map(choices.map((choice) => [choice.key, choice.answer]))
  const values = Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer]
  const labels = values.map((value) => {
    const answerValue = value != null && typeof value === 'object' && 'answer' in value
      ? value.answer
      : value
    if (isBoolean && booleanLabels) {
      if (answerValue === true || answerValue === 'true') return booleanLabels.true
      if (answerValue === false || answerValue === 'false') return booleanLabels.false
    }
    if (value && typeof value === 'object') {
      const answer = (value as { answer?: unknown }).answer
      if (typeof answer === 'string') return answer
    }
    const raw = String(value ?? '')
    return labelsByKey.get(raw) ?? raw
  })
  return labels.filter(Boolean).join(', ') || formatAnswer(correctAnswer)
}

export function QuestionResultCard({
  order,
  isCorrect,
  partialCredit,
  questionSnapshot,
  studentAnswer,
  correctAnswer,
  explanation,
  variant,
  hideStudentAnswer,
}: Props) {
  const { t } = useTranslation('coaching')
  const stem = snapshotText(questionSnapshot)
  const choices = snapshotChoices(questionSnapshot)
  const isPartial = partialCredit != null && partialCredit > 0 && partialCredit < 1
  const showCorrectness = variant === 'meta' && typeof isCorrect === 'boolean' && !isPartial
  const isPendingGrading = variant === 'meta' && isCorrect === null && !isPartial
  return (
    <div
      className={clsx(
        'rounded border p-3 space-y-1.5',
        showCorrectness && isCorrect && 'border-green-200 bg-green-50',
        showCorrectness && !isCorrect && 'border-red-200 bg-red-50',
        isPartial && 'border-amber-200 bg-amber-50',
        isPendingGrading && 'border-amber-200 bg-amber-50',
        !showCorrectness && !isPartial && !isPendingGrading && 'border-gray-200 bg-white',
      )}
    >
      <div className="flex items-center justify-between">
        {order != null && <span className="text-xs font-medium">Q{order + 1}</span>}
        {showCorrectness && (
          <span className={clsx('text-xs font-medium', isCorrect ? 'text-green-700' : 'text-red-700')}>
            {isCorrect ? t('quiz.correct') : t('quiz.incorrect')}
          </span>
        )}
        {isPartial && (
          <span className="text-xs font-medium text-amber-700">{t('quiz.partial')}</span>
        )}
        {isPendingGrading && (
          <span className="text-xs font-medium text-amber-700">{t('quiz.pendingGrading')}</span>
        )}
      </div>
      <div className="text-sm text-gray-800 whitespace-pre-wrap">
        {stem ? <KatexText text={stem} /> : <span className="text-gray-400">{t('placeholder.comingSoon')}</span>}
      </div>
      {choices.length > 0 && (
        <ol className="space-y-0.5 text-xs text-gray-700">
          {choices.map((choice, index) => (
            <li key={`${choice.key}-${index}`}>
              {index + 1}. <KatexText text={choice.answer} />
            </li>
          ))}
        </ol>
      )}
      <div className="text-xs space-y-0.5">
        {!hideStudentAnswer && (
          <div>
            <span className="text-gray-500">{t('quiz.studentAnswer')} </span>
            <span className="text-gray-900"><KatexRenderer text={formatAnswer(studentAnswer)} /></span>
          </div>
        )}
        {variant === 'meta' && correctAnswer !== undefined && (
          <div>
            <span className="text-gray-500">{t('quiz.correctAnswer')} </span>
            <span className="font-semibold text-red-600"><KatexRenderer text={displayCorrectAnswer(correctAnswer, questionSnapshot, {
              true: t('solutionPreview.booleanTrue'),
              false: t('solutionPreview.booleanFalse'),
            })} /></span>
          </div>
        )}
        {variant === 'meta' && explanation && (
          <div>
            <span className="text-gray-500">{t('quiz.explanation')} </span>
            <span className="text-gray-700"><KatexText text={explanation} /></span>
          </div>
        )}
      </div>
    </div>
  )
}
